import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ChatflowResponse } from '../models/chatflow.model';
import { ChatflowScheduleRange } from '../models/chatflow-schedule.model';
import { ChatflowHoliday } from '../models/chatflow-holiday.model';
import {
  ChatflowAgentPerformance,
  ChatflowAnalyticsSummary,
  ChatflowBotPerformance,
  ChatflowNodePerformance,
  ChatflowQueueAnalytics
} from '../models/chatflow-analytics.model';
import {
  CSAT_SCALE_LABELS,
  ChatflowCsatConfigResponse,
  ChatflowCsatMetricsResponse,
  toRatingDistributionRows
} from '../models/chatflow-csat.model';
import { ChatflowScheduleApiService } from '../services/chatflow-schedule-api.service';
import { ChatflowHolidayApiService } from '../services/chatflow-holiday-api.service';
import { ChatflowAnalyticsApiService } from '../services/chatflow-analytics-api.service';
import { ChatflowCsatApiService } from '../services/chatflow-csat-api.service';
import { ChatflowHistoryApiService } from '../services/chatflow-history-api.service';
import {
  ChatflowCommentResponse,
  ChatflowVersionResponse,
  ChatflowVersionRestoreResponse
} from '../models/chatflow-history.model';
import { extractApiErrorMessage } from '../services/chatflow-error.util';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { FormsModule } from '@angular/forms';
import { getChatflowPriorityLabel } from '../services/chatflow-label.util';
import { BadgeColor } from '../../../shared/components/ui/badge/badge.component';

/** Pestañas de la vista detalle, alineadas con el mockup de QA. */
export type DetailTab = 'info' | 'analitica' | 'csat' | 'versiones' | 'comentarios';

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const MODO_FUERA_HORARIO: Record<string, string> = {
  CAPTURE_EMAIL: 'Capturar el correo del visitante',
  SHOW_OFFLINE_MESSAGE: 'Mostrar aviso y dejar chatear',
  MESSAGE_ONLY: 'Mostrar solo un mensaje',
  HIDE_WIDGET: 'Ocultar el widget',
  BOT_ONLY: 'Atender solo con el bot',
  CREATE_TICKET: 'Crear un ticket'
};

/**
 * Vista detalle del chatflow, con la estructura del mockup de QA y el mismo patrón que el
 * resto de detalles del sistema (`contacto-detail-modal`, `deal-detail-modal`): cabecera con
 * identidad + responsable, y secciones con título en versalitas.
 *
 * <p>Sustituye al modal plano anterior, que era una única lista de campos y que QA marcó como
 * "no consistente con el sistema" (INC_CRM_019 (3) inc21).</p>
 *
 * <p>Tres pestañas, todas con datos reales: Información (disponibilidad y métricas de sus
 * endpoints), Historial de versiones (una fila por publicación) y Comentarios (notas del
 * equipo). Las dos últimas necesitaron modelo nuevo en backend — antes no existía forma de
 * saber quién publicó qué ni de dejar notas sobre un chatflow.</p>
 */
@Component({
  selector: 'app-chatflow-detail-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent, BadgeComponent],
  templateUrl: './chatflow-detail-modal.component.html'
})
export class ChatflowDetailModalComponent {
  private readonly scheduleApi = inject(ChatflowScheduleApiService);
  private readonly holidayApi = inject(ChatflowHolidayApiService);
  private readonly analyticsApi = inject(ChatflowAnalyticsApiService);
  private readonly csatApi = inject(ChatflowCsatApiService);
  private readonly historyApi = inject(ChatflowHistoryApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  @Input() isOpen = false;
  @Input() templateName = '—';
  /** Nombre del creador ya resuelto por la lista; evita que el modal repita el lookup. */
  @Input() ownerName = '';

  @Input() set chatflow(value: ChatflowResponse | null) {
    this.chatflowActual = value;
    this.schedules = [];
    this.holidays = [];
    this.summary = null;
    this.versions = [];
    this.comments = [];
    this.activeTab = 'info';
    this.nuevoComentario = '';
    this.comentarioEnEdicion = null;
    this.reiniciarAnalitica();
    this.reiniciarCsat();
    if (value) {
      this.cargarDatosRelacionados(value.id);
      this.cargarHistorial(value.id);
    }
  }
  get chatflow(): ChatflowResponse | null {
    return this.chatflowActual;
  }

  @Output() close = new EventEmitter<void>();
  /** Pestaña del editor a la que saltar desde los accesos del pie. */
  @Output() openEditor = new EventEmitter<'general' | 'flujo' | 'analitica'>();
  /** Se emite tras restaurar: la lista recarga el chatflow, cuyo grafo cambió. */
  @Output() restored = new EventEmitter<void>();

  private chatflowActual: ChatflowResponse | null = null;

  schedules: ChatflowScheduleRange[] = [];
  holidays: ChatflowHoliday[] = [];
  summary: ChatflowAnalyticsSummary | null = null;
  loadingRelated = false;

  // ── Pestañas del mockup (INC_CRM_019 (3) inc21) ─────────────────────────────

  activeTab: DetailTab = 'info';
  versions: ChatflowVersionResponse[] = [];
  comments: ChatflowCommentResponse[] = [];
  loadingHistory = false;

  nuevoComentario = '';
  guardandoComentario = false;
  /** Comentario que se está editando en línea; null = ninguno. */
  comentarioEnEdicion: { id: number; texto: string } | null = null;

  /**
   * Las pestañas de Analítica y CSAT cargan al abrirse por primera vez, no al abrir el
   * modal: son 7 llamadas que la mayoría de las veces nadie mira, y el detalle debe
   * aparecer al instante.
   */
  onTabChange(tab: DetailTab): void {
    this.activeTab = tab;
    const chatflowId = this.chatflowActual?.id;
    if (!chatflowId) return;
    if (tab === 'analitica' && !this.analiticaCargada) {
      this.cargarAnalitica(chatflowId);
    }
    if (tab === 'csat' && !this.csatCargado) {
      this.cargarCsat(chatflowId);
    }
  }

  /** Número de la última publicación, para el bloque "Estado del chatflow". */
  get versionActual(): string {
    return this.versions.length > 0 ? `v${this.versions[0].versionNumber}` : 'Sin publicar';
  }

  private cargarHistorial(chatflowId: number): void {
    this.loadingHistory = true;
    forkJoin({
      versions: this.historyApi.getVersions(chatflowId).pipe(catchError(() => of([] as ChatflowVersionResponse[]))),
      comments: this.historyApi.getComments(chatflowId).pipe(catchError(() => of([] as ChatflowCommentResponse[])))
    }).subscribe((datos) => {
      this.versions = datos.versions ?? [];
      this.comments = datos.comments ?? [];
      this.loadingHistory = false;
    });
  }

  // ── Restaurar una versión ───────────────────────────────────────────────────

  /** Versión que se está restaurando; deshabilita el resto de botones mientras tanto. */
  restaurandoVersionId: number | null = null;

  /**
   * Pide primero el impacto al backend y lo enseña DENTRO de la confirmación: restaurar
   * puede dejar a visitantes con una conversación abierta en un paso que se desactiva, y
   * eso hay que saberlo antes de aceptar, no después.
   */
  async restaurarVersion(version: ChatflowVersionResponse): Promise<void> {
    const chatflowId = this.chatflowActual?.id;
    if (!chatflowId || this.restaurandoVersionId) return;

    this.restaurandoVersionId = version.id;
    let impacto: ChatflowVersionRestoreResponse;
    try {
      impacto = await firstValueFrom(this.historyApi.previewRestore(chatflowId, version.id));
    } catch (err) {
      this.restaurandoVersionId = null;
      this.toast.error({ title: 'Error', message: extractApiErrorMessage(err, 'No se pudo calcular el impacto.') });
      return;
    }

    const detalle = [
      `Se sobrescribirán ${impacto.nodosActualizados} paso(s) del flujo actual.`,
      impacto.nodosCreados > 0 ? `Se volverán a crear ${impacto.nodosCreados} paso(s).` : '',
      impacto.nodosDesactivados > 0
        ? `${impacto.nodosDesactivados} paso(s) que no existían en esa versión quedarán desactivados (no se eliminan).`
        : '',
      impacto.conversacionesAfectadas > 0
        ? `⚠ ${impacto.conversacionesAfectadas} conversación(es) abierta(s) están paradas en uno de esos pasos y dejarán de avanzar.`
        : 'Ninguna conversación abierta se ve afectada.'
    ].filter(Boolean).join(' ');

    const confirmado = await this.confirmDialog.confirmDanger({
      title: `Restaurar la versión v${version.versionNumber}`,
      message: detalle,
      confirmText: 'Restaurar'
    });
    if (!confirmado) {
      this.restaurandoVersionId = null;
      return;
    }

    this.historyApi.restoreVersion(chatflowId, version.id).subscribe({
      next: (resultado) => {
        this.restaurandoVersionId = null;
        this.toast.success({
          title: `Versión v${resultado.versionNumber} restaurada`,
          message: 'El flujo volvió al contenido de esa versión. Publícalo para que salga a producción.'
        });
        // Restaurar no crea una versión nueva: el historial no cambia hasta publicar.
        this.restored.emit();
      },
      error: (err) => {
        this.restaurandoVersionId = null;
        this.toast.error({ title: 'Error', message: extractApiErrorMessage(err, 'No se pudo restaurar la versión.') });
      }
    });
  }

  // ── Comentarios ─────────────────────────────────────────────────────────────

  agregarComentario(): void {
    const texto = this.nuevoComentario.trim();
    const chatflowId = this.chatflowActual?.id;
    if (!texto || !chatflowId || this.guardandoComentario) {
      return;
    }
    this.guardandoComentario = true;
    this.historyApi.createComment(chatflowId, { contentText: texto }).subscribe({
      next: (comentario) => {
        // El backend devuelve la lista más reciente primero: el nuevo va al principio.
        this.comments = [comentario, ...this.comments];
        this.nuevoComentario = '';
        this.guardandoComentario = false;
      },
      error: (err) => {
        this.guardandoComentario = false;
        this.toast.error({ title: 'Error', message: extractApiErrorMessage(err, 'No se pudo publicar el comentario.') });
      }
    });
  }

  empezarEdicion(comentario: ChatflowCommentResponse): void {
    this.comentarioEnEdicion = { id: comentario.id, texto: comentario.contentText };
  }

  cancelarEdicion(): void {
    this.comentarioEnEdicion = null;
  }

  guardarEdicion(): void {
    const edicion = this.comentarioEnEdicion;
    const chatflowId = this.chatflowActual?.id;
    if (!edicion || !chatflowId || !edicion.texto.trim()) {
      return;
    }
    this.historyApi.updateComment(chatflowId, edicion.id, { contentText: edicion.texto.trim() }).subscribe({
      next: (actualizado) => {
        this.comments = this.comments.map((c) => (c.id === actualizado.id ? actualizado : c));
        this.comentarioEnEdicion = null;
      },
      error: (err) => this.toast.error({
        title: 'Error',
        message: extractApiErrorMessage(err, 'No se pudo editar el comentario.')
      })
    });
  }

  async eliminarComentario(comentario: ChatflowCommentResponse): Promise<void> {
    const chatflowId = this.chatflowActual?.id;
    if (!chatflowId) return;

    const confirmado = await this.confirmDialog.confirmDanger({
      title: 'Eliminar comentario',
      message: 'Se eliminará esta nota del equipo. Esta acción no se puede deshacer.'
    });
    if (!confirmado) return;

    this.historyApi.deleteComment(chatflowId, comentario.id).subscribe({
      next: () => this.comments = this.comments.filter((c) => c.id !== comentario.id),
      error: (err) => this.toast.error({
        title: 'Error',
        message: extractApiErrorMessage(err, 'No se pudo eliminar el comentario.')
      })
    });
  }

  readonly priorityLabel = getChatflowPriorityLabel;

  typeLabel(tipo: string | undefined): string {
    if (tipo === 'LIVE_CHAT') return 'Chat en vivo';
    if (tipo === 'AI_CHATBOT') return 'Chatbot IA';
    return 'Bot';
  }

  statusLabel(estado: string | undefined): string {
    if (estado === 'ACTIVE') return 'Publicado';
    if (estado === 'ARCHIVED') return 'Archivado';
    return 'Borrador';
  }

  statusColor(estado: string | undefined): BadgeColor {
    if (estado === 'ACTIVE') return 'success';
    if (estado === 'ARCHIVED') return 'dark';
    return 'warning';
  }

  validationLabel(validacion: string | undefined): string {
    if (validacion === 'VALID') return 'Válido';
    if (validacion === 'INVALID') return 'Inválido';
    if (validacion === 'PENDING') return 'Pendiente de validar';
    return 'Desconocido';
  }

  validationColor(validacion: string | undefined): BadgeColor {
    if (validacion === 'VALID') return 'success';
    if (validacion === 'INVALID') return 'error';
    if (validacion === 'PENDING') return 'warning';
    return 'light';
  }

  /** Inicial para el avatar de la cabecera, igual que en el resto de detalles. */
  get inicial(): string {
    const nombre = this.chatflowActual?.name?.trim();
    return nombre ? nombre.charAt(0).toUpperCase() : 'C';
  }

  /**
   * Carga disponibilidad y métricas. Cada llamada absorbe su propio error: un chatflow sin
   * horario o sin conversaciones es normal y no debe dejar el detalle en blanco.
   */
  private cargarDatosRelacionados(chatflowId: number): void {
    this.loadingRelated = true;
    const rango = this.rangoUltimos30();

    forkJoin({
      schedules: this.scheduleApi.getSchedule(chatflowId).pipe(catchError(() => of([] as ChatflowScheduleRange[]))),
      holidays: this.holidayApi.getHolidays(chatflowId).pipe(catchError(() => of([] as ChatflowHoliday[]))),
      summary: this.analyticsApi.getSummary(chatflowId, rango)
        .pipe(catchError(() => of(null)))
    }).subscribe((datos) => {
      this.schedules = datos.schedules ?? [];
      this.holidays = datos.holidays ?? [];
      this.summary = datos.summary;
      this.loadingRelated = false;
    });
  }

  // ── Disponibilidad ──────────────────────────────────────────────────────────

  get horarioResumen(): string {
    const activos = this.schedules.filter((rango) => rango.active !== false);
    if (activos.length === 0) {
      return 'Sin horario configurado — disponible siempre';
    }
    return activos
      .slice()
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
      .map((rango) => `${DIAS_SEMANA[rango.dayOfWeek] ?? '—'} ${this.hora(rango.startTime)}–${this.hora(rango.endTime)}`)
      .join(' · ');
  }

  get zonaHoraria(): string {
    return this.schedules.find((rango) => !!rango.timezone)?.timezone || 'No configurada';
  }

  get feriadosResumen(): string {
    const activos = this.holidays.filter((feriado) => feriado.active !== false);
    if (activos.length === 0) {
      return 'Sin días festivos configurados';
    }
    return activos.length === 1 ? '1 día festivo configurado' : `${activos.length} días festivos configurados`;
  }

  get mensajeFueraDeHorario(): string {
    const conMensaje = this.schedules.find((rango) => !!rango.outsideHoursMessage?.trim());
    if (conMensaje?.outsideHoursMessage) {
      return conMensaje.outsideHoursMessage.trim();
    }
    const modo = this.schedules.find((rango) => !!rango.outsideHoursMode)?.outsideHoursMode;
    return modo ? (MODO_FUERA_HORARIO[modo] ?? modo) : 'No configurado';
  }

  /** 'HH:mm:ss' → 'HH:mm'. */
  private hora(valor: string | null | undefined): string {
    return valor ? valor.slice(0, 5) : '—';
  }

  // ── Métricas clave ──────────────────────────────────────────────────────────

  get hayMetricas(): boolean {
    return !!this.summary && this.summary.totalConversations > 0;
  }

  /**
   * Todas las tasas de chatflow (analytics y CSAT) llegan ya en porcentaje 0-100 — verificado
   * contra ChatflowAnalyticsService y ChatflowCsatService, que multiplican por 100. Aquí solo
   * se redondea: multiplicarlas otra vez es lo que producía el "8000 %" del primer intento.
   */
  porcentaje(valor: number | null | undefined): string {
    if (valor === null || valor === undefined) return '—';
    return `${Math.round(valor)} %`;
  }

  // ── Analítica (INC_CRM_019 (3) inc21 retest) ────────────────────────────────

  botPerf: ChatflowBotPerformance | null = null;
  queueStats: ChatflowQueueAnalytics | null = null;
  nodePerf: ChatflowNodePerformance[] = [];
  agentPerf: ChatflowAgentPerformance[] = [];
  loadingAnalytics = false;
  /** Mensaje si la analítica no se pudo traer: un 403 no es "todavía no hay datos". */
  analyticsError = '';
  private analiticaCargada = false;

  private reiniciarAnalitica(): void {
    this.botPerf = null;
    this.queueStats = null;
    this.nodePerf = [];
    this.agentPerf = [];
    this.analyticsError = '';
    this.analiticaCargada = false;
  }

  /**
   * Detalle de analítica del mismo periodo que las métricas clave (últimos 30 días). Es una
   * lectura: los filtros por canal/equipo/agente viven en la pestaña Analítica del editor,
   * a la que se llega con el botón del pie.
   */
  private cargarAnalitica(chatflowId: number): void {
    this.loadingAnalytics = true;
    this.analiticaCargada = true;
    this.analyticsError = '';
    const rango = this.rangoUltimos30();
    let falloAlguna = false;
    const anotarFallo = () => { falloAlguna = true; };

    forkJoin({
      summary: this.summary
        ? of(this.summary)
        : this.analyticsApi.getSummary(chatflowId, rango)
            .pipe(catchError(() => { anotarFallo(); return of(null as ChatflowAnalyticsSummary | null); })),
      bot: this.analyticsApi.getBotPerformance(chatflowId, rango)
        .pipe(catchError(() => { anotarFallo(); return of(null as ChatflowBotPerformance | null); })),
      queue: this.analyticsApi.getQueueAnalytics(chatflowId, rango)
        .pipe(catchError(() => { anotarFallo(); return of(null as ChatflowQueueAnalytics | null); })),
      nodes: this.analyticsApi.getNodePerformance(chatflowId, rango)
        .pipe(catchError(() => { anotarFallo(); return of([] as ChatflowNodePerformance[]); })),
      agents: this.analyticsApi.getAgentPerformance(chatflowId, rango)
        .pipe(catchError(() => { anotarFallo(); return of([] as ChatflowAgentPerformance[]); }))
    }).subscribe((datos) => {
      this.summary = datos.summary ?? this.summary;
      this.botPerf = datos.bot;
      this.queueStats = datos.queue;
      this.nodePerf = datos.nodes ?? [];
      this.agentPerf = datos.agents ?? [];
      this.loadingAnalytics = false;
      if (falloAlguna && !this.botPerf && !this.queueStats && this.nodePerf.length === 0) {
        this.analyticsError = 'No se pudo cargar la analítica de este chatflow.';
      }
    });
  }

  get hayAnalitica(): boolean {
    return !!this.summary || !!this.botPerf || !!this.queueStats
      || this.nodePerf.length > 0 || this.agentPerf.length > 0;
  }

  /** Pasos con más abandono: es lo que se mira primero para arreglar un flujo. */
  get nodosConMasAbandono(): ChatflowNodePerformance[] {
    return this.nodePerf.slice().sort((a, b) => (b.dropoffCount ?? 0) - (a.dropoffCount ?? 0)).slice(0, 5);
  }

  get agentesDestacados(): ChatflowAgentPerformance[] {
    return this.agentPerf.slice()
      .sort((a, b) => (b.conversationsHandled ?? 0) - (a.conversationsHandled ?? 0))
      .slice(0, 5);
  }

  /** Segundos → '45 s' / '3 min', igual que en la pestaña Analítica del editor. */
  duracion(segundos: number | null | undefined): string {
    if (segundos === null || segundos === undefined || segundos <= 0) return '—';
    return segundos < 60 ? `${Math.round(segundos)} s` : `${Math.round(segundos / 60)} min`;
  }

  numero(valor: number | null | undefined): string {
    return valor === null || valor === undefined ? '—' : `${valor}`;
  }

  decimal(valor: number | null | undefined): string {
    return valor === null || valor === undefined ? '—' : valor.toFixed(1);
  }

  // ── CSAT ────────────────────────────────────────────────────────────────────

  csatConfig: ChatflowCsatConfigResponse | null = null;
  csatMetrics: ChatflowCsatMetricsResponse | null = null;
  loadingCsat = false;
  csatError = '';
  private csatCargado = false;

  private reiniciarCsat(): void {
    this.csatConfig = null;
    this.csatMetrics = null;
    this.csatError = '';
    this.csatCargado = false;
  }

  /**
   * Config + métricas: sin la config no se puede distinguir "la encuesta está apagada" de
   * "está encendida pero nadie ha respondido", que es justo lo que hay que decirle al usuario.
   */
  private cargarCsat(chatflowId: number): void {
    this.loadingCsat = true;
    this.csatCargado = true;
    this.csatError = '';
    let falloMetricas = false;

    forkJoin({
      config: this.csatApi.getConfig(chatflowId).pipe(catchError(() => of(null))),
      metrics: this.csatApi.getMetrics(chatflowId, this.rangoUltimos30()).pipe(catchError(() => {
        falloMetricas = true;
        return of(null);
      }))
    }).subscribe((datos) => {
      this.csatConfig = datos.config;
      this.csatMetrics = datos.metrics;
      this.loadingCsat = false;
      if (falloMetricas && !datos.config) {
        this.csatError = 'No se pudo cargar la información de CSAT de este chatflow.';
      }
    });
  }

  /** El backend deja `enabled` en null mientras nadie haya guardado la config. */
  get csatActivo(): boolean {
    return this.csatConfig?.enabled === true;
  }

  get csatEscala(): string {
    const escala = this.csatConfig?.ratingScale;
    return escala ? CSAT_SCALE_LABELS[escala] : 'Sin configurar';
  }

  /**
   * Ya no depende de `triggerMode`: la encuesta se pide en cualquier cierre (la cierre un
   * asesor o el flujo) mientras esté configurada. Mostrar el modo antiguo aquí anunciaba
   * una condición que el backend dejó de aplicar.
   */
  get csatDisparador(): string {
    return this.csatActivo
      ? 'Al finalizar la conversación (asesor o flujo)'
      : 'Sin configurar';
  }

  get csatPregunta(): string {
    return this.csatConfig?.questionText?.trim() || 'Sin pregunta personalizada';
  }

  /** Puntuación a partir de la cual una respuesta cuenta como negativa. */
  get csatUmbralNegativo(): string {
    const umbral = this.csatConfig?.negativeThreshold;
    return umbral ? `≤ ${umbral}` : 'Sin configurar';
  }

  get hayRespuestasCsat(): boolean {
    return (this.csatMetrics?.totalResponses ?? 0) > 0;
  }

  /** Filas ordenadas de la distribución de puntuaciones (el backend devuelve un mapa). */
  get distribucionCsat(): { rating: number; count: number }[] {
    return toRatingDistributionRows(this.csatMetrics?.ratingDistribution);
  }

  /**
   * Ancho de la barra de una puntuación, relativo a la más votada. Sin votos la barra se
   * queda a cero: un mínimo visible haría parecer que esa puntuación tuvo respuestas.
   */
  anchoBarraCsat(count: number): string {
    if (!count) return '0%';
    const maximo = Math.max(...this.distribucionCsat.map((fila) => fila.count), 1);
    return `${Math.max(Math.round((count / maximo) * 100), 2)}%`;
  }

  /** Nota media sobre el máximo de la escala configurada, para que el número tenga contexto. */
  get csatPromedio(): string {
    const media = this.csatMetrics?.averageRating;
    if (media === null || media === undefined || !this.hayRespuestasCsat) return '—';
    const escala = this.csatConfig?.ratingScale;
    const maximo = escala === 'NUMERIC_10' ? 10 : escala === 'EMOJI_3' ? 3 : 5;
    return `${media.toFixed(1)} / ${maximo}`;
  }

  onClose(): void {
    this.close.emit();
  }

  /** Ventana de los últimos 30 días, la misma para métricas clave, analítica y CSAT. */
  private rangoUltimos30(): { from: string; to: string } {
    const hoy = new Date();
    const hace30 = new Date(hoy.getTime() - 30 * 24 * 60 * 60 * 1000);
    const iso = (fecha: Date) => fecha.toISOString().slice(0, 10);
    return { from: iso(hace30), to: iso(hoy) };
  }

  irAEditor(tab: 'general' | 'flujo' | 'analitica'): void {
    this.openEditor.emit(tab);
  }
}
